/* ESLint errors suppressed for #4183 */
/* eslint-disable css-modules/no-undef-class */
/* eslint css-modules/no-unused-class: [2, { markAsUsed: ['giftCard'] }] */ /* This class is used in the other uses of stylePickerDropdown.scss */
import React, { Component } from 'react';
import PropTypes from 'prop-types';
import cn from 'classnames';
import { connect } from 'react-redux';
import ExecutionEnvironment from 'exenv';
import template from 'lodash.template';

import AriaLive from 'components/common/AriaLive';
import { capitalize, head } from 'helpers/lodashReplacement';
import { trackEvent } from 'helpers/analytics';
import { setRecommendedFit } from 'actions/productdetail/sizingChooser';
import { fireOnDemandEvent } from 'actions/productdetail/onDemandSizing';
import { fireSizingImpression, productSizeChanged, validateDimensions } from 'actions/productDetail';
import { openPopup } from 'helpers';
import { MoreInfo } from 'components/icons';
import Tooltip from 'components/common/Tooltip';
import styles from 'styles/components/productdetail/stylePickerDropdown.scss';
import SizingRecommendationTooltip from 'components/productdetail/stylepicker/SizingRecommendationTooltip';
import RecommendedSizing from 'components/productdetail/stylepicker/RecommendedSizing';
import RecommendedSizeFinderModal from 'components/productdetail/stylepicker/RecommendedSizeFinderModal';
import ProductUtils from 'helpers/ProductUtils';
import marketplace from 'cfg/marketplace.json';
import { isDesktop } from 'helpers/ClientUtils';

const VALID_DISPLAY_GENDERS = ['Men', 'Mens', 'Women', 'Womens'];
const NORMALIZED_GENDER = {
  'Men': 'Men',
  'Mens': 'Men',
  'Women': 'Women',
  'Womens': 'Women'
};

function makePlaceholder(sizingPlaceholder, gender, dimensionName) {
  const compiled = template(sizingPlaceholder);
  const placeholder = compiled({ gender, dimensionName: capitalize(dimensionName) });
  return placeholder.replace(/\s+/g, ' ');
}

function makeSizingOption(option, sizingPredictionId) {
  const { id, value } = option;
  // The markup needs to be returned separately otherwise it causes a re-rendering error:
  // https://github01.zappos.net/mweb/marty/issues/3175
  return (sizingPredictionId && sizingPredictionId === id) ?
    <option key={id} value={id}>{`${value} - Recommended Size`}</option> :
    <option key={id} value={id}>{value}</option>;
}

function makeSingleValue(option, dimensionId, dimensionName, testId) {
  // option _could_ be undefined if the product is incorrectly setup.  Don't blow up if it setup wrong.
  option = option || { value: '', id: '' };
  return (
    <div className={styles.styleChooserText} data-test-id={testId(`${dimensionId}-singleValue`)}>
      <input type="hidden" name={dimensionId} value={option.id}/>
      <span className={styles.dimensionLabel}>{capitalize(dimensionName)}: </span>
      {option.value}
    </div>
  );
}

export class SizingChooser extends Component {
  state = {
    isOnDemandSizingModalOpen: false
  };

  componentDidUpdate(prevProps) {
    const { product: { isOnDemandEligible, sizingPredictionId, sizingPredictionValue } } = this.props;
    const { fireSizingImpression, product: { sizingPredictionId: prevSizingPredictionId, isOnDemandEligible: prevIsOnDemandEligible } } = prevProps;
    if (sizingPredictionId !== prevSizingPredictionId && sizingPredictionValue) {
      fireSizingImpression('DIRECT_PREDICTION', { id: sizingPredictionId, value: sizingPredictionValue });
    } else if (isOnDemandEligible && isOnDemandEligible !== prevIsOnDemandEligible) {
      fireSizingImpression('ON_DEMAND_ELIGIBLE');
    }
  }

  handleCalculateSizeClick = () => {
    const { fireOnDemandEvent } = this.props;
    fireOnDemandEvent('start');
    this.setState({ isOnDemandSizingModalOpen: true });
  };

  handleCloseModalClick = () => {
    this.setState({ isOnDemandSizingModalOpen: false });
  };

  handleSetRecommendedFit = predictedSize => {
    const { product, setRecommendedFit, productSizeChanged, fireSizingImpression } = this.props;
    this.setState({ isOnDemandSizingModalOpen: false });
    setRecommendedFit(predictedSize);

    const { selectedSizing, detail: { productId, sizing: { dimensions } } } = product;
    const newSelectedSizing = { ...selectedSizing };

    const sizeDimension = dimensions.find(dim => dim.name === 'size');
    const dimValues = sizeDimension.units[0].values;
    const sizeObj = dimValues.find(({ value }) => value === predictedSize);

    if (sizeObj) {
      newSelectedSizing[`d${sizeDimension.id}`] = sizeObj.id;
      productSizeChanged(newSelectedSizing);
      validateDimensions();
    }

    fireSizingImpression('ON_DEMAND_PREDICTION', sizeObj);
    trackEvent('TE_PDP_SIZING', `${productId}:SizeSelected:${predictedSize}`);
    trackEvent('TE_PDP_ONDEMAND_SIZING', `${productId}:SizeSuggested:${predictedSize}`);
  };

  isNeededValuesSelected(selectedVal, options, isSelectSizeTooltipVisible) {
    return !selectedVal && options.length > 1 && isSelectSizeTooltipVisible;
  }

  makeLabelMessaging(showSizeGender, gender, dimensionName, needMoreSelections, isSelectSizeTooltipHighlighted) {
    const { testId } = this.context;
    if (needMoreSelections) {
      const text = ProductUtils.buildSizeMessagingText(dimensionName);
      return <>
        <span className={styles.needSelectionLabel} data-test-id={testId('selectionError')}>{text}</span>
        { isSelectSizeTooltipHighlighted && <AriaLive> {text} </AriaLive> }
      </>;
    } else if ((showSizeGender || dimensionName === 'size') && gender !== '') {
      return `${gender} ${dimensionName}`;
    }
    return dimensionName;
  }

  makeRecommendedSizing = (dimensionName, selectedVal, onDemandSizingPredictionId, onDemandSizingGender, sizingPredictionVal) => {
    const { testId } = this.context;
    const { isOnDemandSizingModalOpen } = this.state;
    const { productId, productImage, productTitle, sizingChooser, location, isOnDemandEligible, sizingPredictionId, product } = this.props;
    const { sizingPredictionValue } = product;
    const { recommendedFit } = sizingChooser;
    const predictedSize = recommendedFit || sizingPredictionValue || '';

    if (ExecutionEnvironment.canUseDOM) {
      const tooltipContent = (
        <span className={styles.recommendedSizingTooltipContent}>
          Recommended sizing is based on your selection and the purchases of similar customers. We blend it all together to get you the perfect fit! Hooray!
        </span>
      );

      const buttonText = !isDesktop(marketplace) ? 'Calculate Size' : 'Calculate your size';

      const calculateSizeButton = (
        <button
          className={styles.calculateSize}
          type="button"
          onClick={this.handleCalculateSizeClick}
          data-test-id={testId('onDemandSizingLink')}>
          {buttonText}
        </button>
      );

      const onDemandContent = (
        <RecommendedSizeFinderModal
          productId={productId}
          productImage={productImage}
          productTitle={productTitle}
          gender={onDemandSizingGender}
          onSetRecommendedFit={this.handleSetRecommendedFit}
          onClose={this.handleCloseModalClick}
          location={location}
          handleCalculateSizeClick={this.handleCalculateSizeClick}
          isOpen={isOnDemandSizingModalOpen}
          sizingPredictionVal={sizingPredictionVal}
          predictedSize={predictedSize}/>
      );

      if (!isOnDemandEligible || sizingPredictionId) {
        return null;
      }

      return (
        <div className={styles.recommendedFitContainer}>
          <Tooltip direction="left" content={tooltipContent} tooltipId="recommendedFit">
            <div className={styles.recommendedFit}>
              <strong>Recommended Fit</strong>
              <MoreInfo/>
            </div>
          </Tooltip>
          {recommendedFit ? (
            <div>
              {(dimensionName === 'size' && selectedVal === onDemandSizingPredictionId) ? (
                <span className={styles.recommendedSizeLink}>
                  Your recommended size has been selected.
                </span>
              ) : (
                <span className={styles.recommendedSizeLink}>
                  Your recommended size is <strong>{recommendedFit}</strong>.
                </span>
              )}
            </div>
          ) : calculateSizeButton}
          {onDemandContent}
        </div>
      );
    }
  };

  render() {
    const { testId } = this.context;
    const {
      genders,
      sizing,
      selectedSizing,
      dimensionValidation,
      sizingPlaceholder,
      product,
      productType,
      onSizeChange,
      showSizeGender,
      showSizeCharts,
      isSelectSizeTooltipVisible,
      isSelectSizeTooltipHighlighted,
      sizingPredictionId,
      isOnDemandEligible,
      onDemandSizing,
      hasRecommendedSizing,
      hydraSizeRecommenderTooltip,
      location
    } = this.props;

    const { isOnDemandSizingModalOpen } = this.state;
    const { predictedSize } = onDemandSizing;

    const gender = genders.length === 1 && VALID_DISPLAY_GENDERS.includes(head(genders)) ? head(genders).replace(/s?$/, '\'s') : '';
    const onDemandSizingGender = genders.length === 1 ? NORMALIZED_GENDER[head(genders)] : null;
    const sizeDimension = sizing.dimensions.find(dimension => dimension.name === 'size');
    const showRecommendedSizing = hasRecommendedSizing && sizeDimension && ProductUtils.isShoeType(productType) && gender && (isOnDemandEligible !== false);
    let selectedSize;
    return (
      <>
        { isSelectSizeTooltipHighlighted && <AriaLive> Selection is incomplete. </AriaLive> }
        <div className={styles.stackableStyleChooserSections}>
          {sizing.dimensions.map((dimension, index) => {
            const dimensionId = `d${dimension.id}`;
            const dimensionName = dimension.name;
            const options = sizing.allUnits[index].values;
            const isIncomplete = dimensionValidation[dimensionId];
            let selectedVal = selectedSizing[dimensionId];
            if (options.length === 1) {
              selectedVal = head(options).id;
            }

            let sizingPredictionVal = '';
            for (let i = 0; i < options.length; i++) {
              if (options[i].id === sizingPredictionId) {
                sizingPredictionVal = options[i].value;
              }
            }

            const onDemandSizingPredictionSize = options.find(option => option.value === predictedSize);
            const onDemandSizingPredictionId = onDemandSizingPredictionSize ? onDemandSizingPredictionSize.id : null;
            const pdpDimensionSelectId = `pdp-${dimensionName}-select`;
            const needMoreSelections = this.isNeededValuesSelected(selectedVal, options, isSelectSizeTooltipVisible);

            if (dimensionName === 'size' && sizing.valueIdToName && selectedVal) {
              selectedSize = sizing.valueIdToName[selectedVal]?.value;
            }

            return (
              <div key={dimensionName} className={cn(styles.styleChooserSection, { [styles.invalidDimensionControl]: isIncomplete })} data-test-id={testId(`${dimensionId}-sizingChooser`)}>
                <label className={cn({ [styles.singleVal]: options.length <= 1 })} htmlFor={pdpDimensionSelectId}>
                  {this.makeLabelMessaging(showSizeGender, gender, dimensionName, needMoreSelections, isSelectSizeTooltipHighlighted)}
                  {showSizeCharts && dimensionName === 'size' && ProductUtils.isShoeType(productType) && (
                    <span className={styles.styleChooserSizeChart}>
                      <span> (<a
                        href="/cs/mmf-popop.zml"
                        target="_blank"
                        onClick={openPopup}
                        data-popup-options="width=1000,height=800"
                        data-track-action="Product-Page"
                        data-track-label="Tabs"
                        data-track-value="Size-Chart">Size Chart</a>)</span>
                    </span>
                  )}
                </label>
                {options.length > 1 ? (
                  <div className={styles.styleChooserControlWrapper}>
                    <select
                      id={pdpDimensionSelectId}
                      name={dimensionId}
                      className={cn(styles.styleChooserControl, {
                        [styles.highlighted]: isSelectSizeTooltipHighlighted,
                        [styles.recommendedSizeSelect]: selectedVal === sizingPredictionId || selectedVal === onDemandSizingPredictionId,
                        [styles.needSelection]: needMoreSelections
                      })}
                      value={selectedVal || sizingPredictionId || ''}
                      onChange={onSizeChange.bind(this, options)}
                      data-track-label={dimensionName}>
                      <option value="">{makePlaceholder(sizingPlaceholder, gender, dimensionName)}</option>
                      {options.map(option =>
                        makeSizingOption(option, sizingPredictionId)
                      )}
                    </select>
                    {
                      hasRecommendedSizing &&
                      !hydraSizeRecommenderTooltip &&
                      dimensionName === 'size' &&
                      ProductUtils.isShoeType(productType) &&
                      gender && this.makeRecommendedSizing(dimensionName, selectedVal, onDemandSizingPredictionId, onDemandSizingGender, sizingPredictionVal)
                    }
                    { hasRecommendedSizing && !hydraSizeRecommenderTooltip && dimensionName === 'size' && sizingPredictionVal && (
                      <SizingRecommendationTooltip
                        dimensionName={dimensionName}
                        selectedVal={selectedVal}
                        sizingPredictionId={sizingPredictionId}
                        sizingPredictionVal={sizingPredictionVal}
                        onCalculateSize={this.handleCalculateSizeClick}/>
                    )}
                  </div>
                ) : makeSingleValue(options[0], dimensionId, dimensionName, testId)}
              </div>
            );
          })}
        </div>
        { hydraSizeRecommenderTooltip && showRecommendedSizing &&
          <RecommendedSizing
            onOpenModal={this.handleCalculateSizeClick}
            onCloseModal={this.handleCloseModalClick}
            product={product}
            gender={onDemandSizingGender}
            handleSetRecommendedFit={this.handleSetRecommendedFit}
            selectedSize={selectedSize}
            location={location}
            handleCalculateSizeClick={this.handleCalculateSizeClick}
            isOnDemandSizingModalOpen={isOnDemandSizingModalOpen}/>
        }
      </>
    );
  }
}

SizingChooser.contextTypes = {
  testId: PropTypes.func
};

SizingChooser.propTypes = {
  genders: PropTypes.arrayOf(PropTypes.string),
  sizing: PropTypes.object,
  selectedSizing: PropTypes.object,
  dimensionValidation: PropTypes.object,
  sizingPlaceholder: PropTypes.string,
  productType: PropTypes.string,
  onSizeChange: PropTypes.func,
  showSizeGender: PropTypes.bool,
  showSizeCharts: PropTypes.bool,
  isSelectSizeTooltipVisible: PropTypes.bool
};

const mapStateToProps = state => {
  const { product, sizingChooser: { sizingChooser, onDemandSizing } } = state;
  return {
    product,
    sizingChooser,
    onDemandSizing
  };
};

export default connect(mapStateToProps, {
  fireSizingImpression,
  fireOnDemandEvent,
  setRecommendedFit,
  productSizeChanged,
  validateDimensions
})(SizingChooser);
/* eslint-enable */
